
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {TickClientUpdate} from '../core/TickClientUpdate';
import {FieldModelTickClientUpdate} from '../core/TickClientUpdate';

/**
 * TickUpdateNotify struct
 */
class TickUpdateNotify {
  /**
   * Initialize struct
   * @param {!Array=} ticks
   * @constructor
   */
  constructor (ticks = []) {
    this.Ticks = ticks
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TickUpdateNotify}
   * @param {!TickUpdateNotify} other Other struct
   * @returns {!TickUpdateNotify} This struct
   */
  copy (other) {
    if (other.Ticks != null) {
      this.Ticks = []
      for (let item of other.Ticks) {
        if (item != null) {
          let tempItem
          tempItem = TickClientUpdate.fromObject(item)
          this.Ticks.push(tempItem)
        } else {
          this.Ticks.push(null)
        }
      }
    } else {
      this.Ticks = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TickUpdateNotify}
   * @returns {!TickUpdateNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TickUpdateNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TickUpdateNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TickUpdateNotify}
   * @param {!TickUpdateNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TickUpdateNotify)) {
      throw new TypeError('Instance of TickUpdateNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TickUpdateNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Ticks: ((this.Ticks != null) ? Array.from(this.Ticks, item => ((item != null) ? item : null)) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TickUpdateNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TickUpdateNotify} other Object value
   * @returns {!TickUpdateNotify} Created struct
   */
  static fromObject (other) {
    return new TickUpdateNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TickUpdateNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TickUpdateNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TickUpdateNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5052
  }
}

export { TickUpdateNotify };

/**
 * Fast Binary Encoding TickUpdateNotify field model
 */
class FieldModelTickUpdateNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Ticks = new fbe.FieldModelVector(new FieldModelTickClientUpdate(buffer, 4 + 4), buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelTickUpdateNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTickUpdateNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Ticks.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTickUpdateNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Ticks.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTickUpdateNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTickUpdateNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTickUpdateNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5052
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTickUpdateNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTickUpdateNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Ticks.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Ticks.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Ticks.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTickUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTickUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTickUpdateNotify}
   * @param {!TickUpdateNotify} fbeValue Default value, defaults is new TickUpdateNotify()
   * @returns {!TickUpdateNotify} TickUpdateNotify value
   */
  get (fbeValue = new TickUpdateNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTickUpdateNotify}
   * @param {!TickUpdateNotify} fbeValue TickUpdateNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Ticks.fbeSize) <= fbeStructSize) {
      this.Ticks.get(fbeValue.Ticks)
    } else {
      fbeValue.Ticks.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Ticks.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTickUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTickUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTickUpdateNotify}
   * @param {!TickUpdateNotify} fbeValue TickUpdateNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTickUpdateNotify}
   * @param {TickUpdateNotify} fbeValue TickUpdateNotify value
   */
  setFields (fbeValue) {
    this.Ticks.set(fbeValue.Ticks)
  }
}

export { FieldModelTickUpdateNotify };

/**
 * Fast Binary Encoding TickUpdateNotify model
 */
class TickUpdateNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTickUpdateNotify(this.buffer, 4)
  }

  /**
   * Get the TickUpdateNotify model
   * @this {!TickUpdateNotifyModel}
   * @returns {!FieldModelTickUpdateNotify} model TickUpdateNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TickUpdateNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TickUpdateNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TickUpdateNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TickUpdateNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTickUpdateNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TickUpdateNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TickUpdateNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TickUpdateNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TickUpdateNotifyModel}
   * @param {!TickUpdateNotify} value TickUpdateNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TickUpdateNotifyModel}
   * @param {!TickUpdateNotify} value TickUpdateNotify value, defaults is new TickUpdateNotify()
   * @return {!object} Deserialized TickUpdateNotify value and its size
   */
  deserialize (value = new TickUpdateNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TickUpdateNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TickUpdateNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TickUpdateNotifyModel}
   * @param {!number} prev Previous TickUpdateNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TickUpdateNotifyModel };
